<script module lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Faqs } from '#library/components.schema.build.ts'
	import Accord from '#components/accord.svelte'
	import Filterbar from '#components/filterbar.svelte'
	import { SvelteMap } from 'svelte/reactivity'
	import * as _ from 'radashi'
	import { page } from '$app/state'
	import { params } from '#library/params.svelte.ts'

	let instance = 0
	let filter_map = new SvelteMap<string, string>()
</script>

<script lang="ts">
	interface Props {
		blok: Blok<Faqs>
	}

	let { blok }: Props = $props()

	const first = instance === 0 ? true : false
	// eslint-disable-next-line no-useless-assignment
	instance++

	$effect(() => {
		for (const item of blok.items || []) {
			if (!item.title) continue

			// Store as key, since item.title sometimes has different casing due to user input inconsistency
			const ref = _.snake(item.title)

			// Only add if it doesn't already exist
			if (!filter_map.has(ref)) filter_map.set(ref, item.title)
		}
	})

	const filters = $derived(
		Array.from(filter_map.values()).map(title => ({
			label: title,
			href: params({ type: title }),
			active: page.url.searchParams.get('type') === title
		}))
	)

	const active_filter = $derived(filters.find(filter => filter.active))

	const items = $derived.by(() => {
		let items = blok.items

		if (active_filter) {
			items = blok.items?.filter(item => item.title?.toLowerCase() === active_filter?.label?.toLowerCase())
		}

		const search = page.url.searchParams.get('search')

		if (search) {
			const results = []

			for (const item of items || []) {
				// check if any block title matches the search
				const has_matching_block = item.blocks?.some(
					block =>
						'title' in block &&
						typeof block.title === 'string' &&
						block.title?.toLowerCase().includes(search.toLowerCase())
				)

				if (has_matching_block) {
					// filter the blocks to only show matching ones
					const filtered_item = {
						...item,
						blocks: item.blocks?.filter(
							block =>
								'title' in block &&
								typeof block.title === 'string' &&
								block.title?.toLowerCase().includes(search.toLowerCase())
						)
					}
					results.push(filtered_item)
				}
			}

			items = results
		}

		return items
	})
</script>

{#if items?.length || first}
	<section class="faqs {blok.theme}" {...attrs(blok)}>
		{#if first}
			<Filterbar {filters} />
		{/if}
		<div class="contain">
			{#if items?.length}
				<div class="title">{blok.title}</div>
				<div class="items">
					{#each items as item, i (i)}
						<div class="item">
							<div class="subtitle">{item.title}</div>
							{#if item.blocks?.length}
								{#each item.blocks as block, i (i)}
									{#if 'title' in block && 'body' in block}
										<Accord title={block.title} body={block.body} size="small" />
									{/if}
								{/each}
							{/if}
						</div>
					{/each}
				</div>
			{/if}
			{#if first && !items?.length}
				<p>No results found</p>
			{/if}
		</div>
	</section>
{/if}

<style lang="css">
	.faqs {
		background-color: var(--c-background);
		color: var(--c-midground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));

		&:has(~ :global(.faqs)) {
			padding-block-end: var(--s-40);
		}

		~ :global(.faqs) {
			padding-block-start: var(--s-40);
		}
	}

	.contain {
		display: grid;
		gap: 2.5rem;

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);
		margin-block-end: 0.5rem;
	}

	.items {
		display: flex;
		flex-direction: column;
		gap: 2rem;
	}

	.item {
		background-color: var(--c-white);
		font: 400 var(--f-h5);
		padding: 2.5rem 3rem;
	}

	.subtitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-end: 1.5rem;
		text-transform: uppercase;
	}
</style>
